import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import ModalBillingEntity from "@components/modules/billing-entity/ModalBillingEntity/ModalBillingEntity";
import { AvatarUpload, FormRow, Select, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type BillingEntityModalFragment,
  type BrandModalFragment,
  useBillingEntityOptionsForModalBrandQuery,
  useClientOptionsForModalBrandQuery,
  useCreateBrandMutation,
  useGetNextNumericalBrandCodeQuery,
  useUpdateBrandMutation,
} from "@src/__generated__/graphql";
import brandPlaceholder from "@src/assets/img/brand.png";
import { onError } from "@src/utils/apollo";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { type FC, useEffect, useMemo, useState } from "react";
import { FormBrand } from "./form";

export type TBrandFillFromClient = Partial<{
  name: string;
  code: string;
  numeric_code: string;
}>;

interface ModalBrandProps {
  isOpen: boolean;
  onClose: () => void;
  brandFill?: TBrandFillFromClient;
  brand?: BrandModalFragment;
  clientId?: string;
  onSubmit: (payload: BrandModalFragment) => void;
  onCreateClientClick?: () => void;
  preventRedirect?: boolean;
  onNameChange?: (val: string) => void;
}

const ModalBrand: FC<ModalBrandProps> = ({
  brandFill,
  isOpen,
  onClose,
  brand,
  clientId,
  onSubmit,
  onCreateClientClick,
  preventRedirect,
  onNameChange,
}) => {
  const { workspaceStore } = useStore();
  const [form] = useState(() => new FormBrand(workspaceStore.settings));
  const router = useRouter();

  useEffect(() => {
    if (!brandFill) return;

    if (!form.name.value?.length) {
      form.name.onChange(brandFill?.name ?? "");
    }

    if (!form.code.value?.length) {
      form.code.onChange(brandFill?.code ?? "");
    }

    if (!form.numeric_code.value?.length) {
      form.numeric_code.onChange(brandFill?.numeric_code ?? "");
    }
  }, [brandFill]);

  useEffect(() => {
    if (brand) {
      form.name.onChange(brand.name);
      form.code.onChange(brand.code);
      form.default_billing_entity_id.onChange(brand.defaultBillingEntity?.id);
      form.numeric_code.onChange(brand.numeric_code ?? "");
    }

    if (clientId) {
      form.client_id.onChange(clientId);
      refetchClientOptions();
    }
  }, [brand, clientId, isOpen]);

  const {
    refetch: refetchClientOptions,
    data: clientOptionsData,
    loading: loadingClientOptions,
  } = useClientOptionsForModalBrandQuery({
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: billingEntityOptionsData,
    loading: loadingBillingEntityOptions,
    refetch: refetchBillingEntityOptions,
  } = useBillingEntityOptionsForModalBrandQuery({
    variables: { client_id: form.client_id.value },
    skip: !isOpen || !form.client_id.value,
    fetchPolicy: "cache-and-network",
  });

  const { loading: loadingBrandCode } = useGetNextNumericalBrandCodeQuery({
    skip:
      !isOpen ||
      !workspaceStore.settings?.generate_numerical_brand_codes ||
      !!brand?.id,
    onCompleted(response) {
      const code = response.getNextNumericalBrandCode;
      if (code && !form.code.value) {
        form.code.onChange(code);
      }
    },
  });

  const onBeforeSave = async () => {
    const { hasError } = await form.validate({ withClient: !clientId });

    if (hasError) return;

    if (brand?.id) {
      update({ variables: { input: form.serializeForUpdate(brand.id) } });
    } else {
      create({ variables: { input: form.serializeForCreate() } });
    }
  };

  const [create, { loading: loadingCreate }] = useCreateBrandMutation({
    onCompleted({ createBrand }) {
      if (!createBrand) return;

      onSubmit(createBrand);
      form._form.reset();

      if (preventRedirect) return;
      router.replace({
        pathname: "/brands/detail",
        query: {
          id: createBrand.id,
        },
      });
    },
    ...onError(),
  });

  const [update, { loading: loadingUpdate }] = useUpdateBrandMutation({
    onCompleted(data) {
      if (data.updateBrand) {
        onSubmit(data.updateBrand);
      }
    },
    ...onError(),
  });

  const onCreateBillingEntity = (payload: BillingEntityModalFragment) => {
    billingEntityModal.onClose();
    form.default_billing_entity_id.onChange(payload.id);
    refetchBillingEntityOptions();
  };

  const billingEntityModal = useDisclosure();

  const title = useMemo(() => (brand ? t`Edit brand` : t`Add brand`), [brand]);

  const clientOptions = useMemo(
    () =>
      clientOptionsData?.clientsSimpleMap.map((i) => ({
        label: i.name,
        value: i.id,
      })) || [],
    [clientOptionsData],
  );

  const billingEntityOptions = useMemo(
    () =>
      billingEntityOptionsData?.client?.billingEntities.map((i) => ({
        label: i.internal_name,
        value: i.id,
      })) || [],
    [billingEntityOptionsData],
  );

  const handleClose = () => {
    onClose();
    form._form.reset();
  };

  const isCodeChanged =
    !!brand &&
    !!form.code.value.trim() &&
    form.code.value.trim().toUpperCase() !== brand.code.toUpperCase();

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen && !billingEntityModal.isOpen}
        onClose={handleClose}
        size="3xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <div className="grid grid-cols-12 gap-12">
              <div className="col-span-3">
                <AvatarUpload
                  roundedFull
                  placeholderImage={brandPlaceholder}
                  imageSrc={brand?.image?.urls.thumbnail || ""}
                  onChange={(val) => {
                    form.photo.onChange(val ? val : "delete");
                  }}
                />
              </div>
              <div className="col-span-9">
                <FormRow title={<Trans>Name</Trans>}>
                  <TextField
                    {...fieldToInputProps(form.name)}
                    placeholder={t`Name`}
                    onChange={(val) => {
                      form.name.onChange(val);
                      onNameChange?.(val);
                    }}
                  />
                </FormRow>

                <FormRow title={<Trans>Code</Trans>}>
                  <TextField
                    placeholder={t`Example: ABC`}
                    isDisabled={loadingBrandCode}
                    {...fieldToInputProps(form.code)}
                  />
                </FormRow>

                {isCodeChanged && (
                  <FormRow title={<Trans>Existing projects</Trans>}>
                    <Checkbox
                      isChecked={form.update_project_codes.value}
                      onChange={(e) =>
                        form.update_project_codes.onChange(e.target.checked)
                      }
                    >
                      <Trans>
                        Rename codes of existing projects in this brand
                      </Trans>
                    </Checkbox>
                    <Text color="grey.400" fontSize="sm" mt="1">
                      <Trans>
                        Project codes will change from{" "}
                        {brand?.code.toUpperCase()}-… to{" "}
                        {form.code.value.trim().toUpperCase()}-…
                      </Trans>
                    </Text>
                  </FormRow>
                )}

                {workspaceStore.settings?.enableAdditionalBrandNumericCode && (
                  <FormRow title={<Trans>Numeric code</Trans>}>
                    <TextField
                      placeholder={t`Example: 123`}
                      value={form.numeric_code.value}
                      onChange={form.numeric_code.onChange}
                      error={form.numeric_code.error}
                    />
                  </FormRow>
                )}

                {!brand && (
                  <FormRow title={<Trans>Client</Trans>}>
                    <Select
                      asPortal
                      placeholder={t`Select client`}
                      isLoading={loadingClientOptions}
                      {...fieldToSelectProps(form.client_id, clientOptions)}
                      topExtraContent={
                        onCreateClientClick && (
                          <Button
                            p="3"
                            onClick={onCreateClientClick}
                            variant="link"
                          >
                            <Trans>+ Add new client</Trans>
                          </Button>
                        )
                      }
                    />
                  </FormRow>
                )}

                <FormRow title={<Trans>Billing entity</Trans>}>
                  <Select
                    asPortal
                    isSearchable
                    placeholder={t`Select billing entity`}
                    isLoading={loadingBillingEntityOptions}
                    isDisabled={!form.client_id.value}
                    {...fieldToSelectProps(
                      form.default_billing_entity_id,
                      billingEntityOptions,
                    )}
                    topExtraContent={
                      <Button
                        p="3"
                        onClick={billingEntityModal.onOpen}
                        variant="link"
                      >
                        <Trans>+ Add new billing entity</Trans>
                      </Button>
                    }
                  />
                </FormRow>
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="grey" onClick={handleClose} variant="outline">
              <Trans>Cancel</Trans>
            </Button>
            <Button
              ml={3}
              isDisabled={form._form.hasError}
              isLoading={loadingCreate || loadingUpdate}
              onClick={onBeforeSave}
            >
              <Trans>Save</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {billingEntityOptionsData?.client && (
        <ModalBillingEntity
          {...billingEntityModal}
          client={billingEntityOptionsData?.client}
          onSubmit={onCreateBillingEntity}
        />
      )}
    </React.Fragment>
  );
};

export default observer(ModalBrand);
